import { tabNames } from '@va/constants';
import { VisitSession } from '@va/dashboard/api-client/Visitors';
import { CountryLocationIcon, VideoRecordingButton, VisitDetails } from '@va/dashboard/components';
import { useCheckPrivacySetting } from '@va/dashboard/util-hooks';
import {
  BrowserIcon,
  DeviceIcon,
  LayersIcon,
  NavVisitors,
  OperatingSystemIcon,
  WebsiteManagementGlobe,
} from '@va/icons';
import { useLocale, useTranslate } from '@va/localization';
import { PRIVACY_FEATURES } from '@va/standalone/shared/types';
import { VisitorTypes } from '@va/types/visitors';
import { fontWeights, GrayGradientDivider, Paragraph, paragraphSizes, VisitedPagesTooltip } from '@va/ui/design-system';
import { TooltipWrapper } from '@va/ui/tooltips';
import { useRefreshContext } from '@va/util/components';
import { useCheckOverflow } from '@va/util/hooks';
import { useMemo, useRef } from 'react';
import Skeleton from 'react-loading-skeleton';
import { mappedVisitorHistoryPageVisits } from './mappedVisitorHistoryPageVisits';
import { SessionDetails } from './SessionDetails';
import useVisitorHistoryContext from './VisitorHistoryContext';

const VisitorsHistoryGridView = () => {
  const { isLoading, data: visitorHistorySessions } = useVisitorHistoryContext();
  const { isManualRefreshing } = useRefreshContext();
  const isInProgress = isLoading || isManualRefreshing;

  return (
    <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5'>
      {isInProgress && (
        <>
          {Array.from(Array(visitorHistorySessions?.length).keys()).map((_, index) => {
            return <Skeleton key={index} className='w-full h-62.5 rounded-18' />;
          })}
        </>
      )}
      {!isInProgress &&
        visitorHistorySessions?.map((session, index: number) => {
          return <VisitorHistoryGridItem key={index} session={session} />;
        })}
    </div>
  );
};

export default VisitorsHistoryGridView;

export const VisitorHistoryGridItem = ({ session }: { session: VisitSession }) => {
  const translate = useTranslate();
  const { locale } = useLocale();

  const {
    duration,
    sessionStart,
    pageVisits,
    status,
    ip,
    location,
    countryCode,
    hasRecording,
    id,
    platform,
    browser,
    device,
    display,
  } = session;

  const { isTrackingEnabled } = useCheckPrivacySetting();
  const isPageHistoryTrackingEnabled = isTrackingEnabled(PRIVACY_FEATURES.individualPageHistory, countryCode);

  const mappedVisitedPages = useMemo(() => mappedVisitorHistoryPageVisits(pageVisits, locale), [locale, pageVisits]);

  return (
    <div className='border border-gray-mercury rounded-18 overflow-hidden flex flex-col justify-between'>
      <div className='flex flex-col gap-3 p-4'>
        <SessionDetails sessionDuration={duration} sessionStartAt={sessionStart} wrapperClassName='gap-3!' />
        <div className='flex flex-col w-auto gap-3 ml-14'>
          <div className='flex flex-row flex-nowrap gap-3'>
            <TooltipWrapper
              interactive
              useDefaultStyle={false}
              content={
                <VisitedPagesTooltip
                  title={translate('card.latestVisitors.visitorsHistory.title')}
                  items={mappedVisitedPages}
                  sessionStart={sessionStart}
                  showVisitPagesAndDate
                  showSubTitle={false}
                  isPageHistoryTrackingEnabled={isPageHistoryTrackingEnabled}
                />
              }
            >
              <div>
                <SessionInfoSection
                  text={translate('visitorsHistory.pages', {
                    pages: pageVisits?.length ?? 0,
                  })}
                  icon={(className) => <LayersIcon className={className} color='#969696' />}
                  showTooltip={false}
                />
              </div>
            </TooltipWrapper>
            <SessionInfoSection
              text={translate(`card.latestVisits.${VisitorTypes[status]}`)}
              icon={(className) => <NavVisitors className={className} color='#969696' />}
            />
          </div>
          <div className='flex flex-row flex-nowrap gap-3'>
            <SessionInfoSection
              text={ip}
              icon={(className) => <WebsiteManagementGlobe className={className} color='#969696' />}
            />
          </div>
        </div>
      </div>
      <div>
        <GrayGradientDivider />
        <div className='flex items-center justify-between gap-1 bg-white-snow p-12px overflow-auto scrollbar scrollbar-thumb'>
          <div className='flex gap-3'>
            <CountryLocationIcon location={location} countryCode={countryCode} sessionId={id} />
            <VisitDetails
              tooltipSubtext={
                <div className='flex flex-col text-center'>
                  <div>{translate(`panels.latestVisitors.deviceType.${device}`)}</div>
                  <div>{display}</div>
                </div>
              }
              tooltipText={translate(`panels.latestVisitors.device`)}
              icon={<DeviceIcon deviceType={device} color='#696969' />}
            />
            <VisitDetails
              tooltipSubtext={platform}
              tooltipText={translate(`panels.latestVisitors.operatingSystem`)}
              icon={<OperatingSystemIcon osName={platform} />}
            />
            <VisitDetails
              tooltipText={translate(`panels.latestVisitors.browser`)}
              tooltipSubtext={browser}
              icon={<BrowserIcon browserName={browser} />}
            />
          </div>
          <VideoRecordingButton
            color={'secondary'}
            sourceTab={tabNames.visitorsHistory}
            sessionInfo={{ unixTs: sessionStart, id, hasRecording: hasRecording }}
          />
        </div>
      </div>
    </div>
  );
};

const SessionInfoSection = ({
  text,
  icon,
  showTooltip = true,
}: {
  text: string;
  icon: (className?: string) => void;
  showTooltip?: boolean;
}) => {
  const textRef = useRef(null);
  const isOverflow = useCheckOverflow(textRef);
  return (
    <TooltipWrapper disabled={!isOverflow || !showTooltip} content={text}>
      <div className='flex items-center gap-1.5 justify-center p-10.5 rounded-12 h-38 bg-white-snow overflow-hidden'>
        <>{icon('h-18 w-18 shrink-0')}</>
        <Paragraph
          ref={textRef}
          colorClassName='text-gray-charcoal'
          size={paragraphSizes.tiny}
          weight={fontWeights.medium}
          className='truncate'
        >
          {text}
        </Paragraph>
      </div>
    </TooltipWrapper>
  );
};
